import { useCallback } from 'react'

import { TagManagerEventType } from '~/platform/utils/googleTagManager/tagManagerModels'
import { triggerTagManagerEvent } from '~/platform/utils/googleTagManager/tagManagerUtils'
import { COST_BOM_DEMO_STRINGS } from '~/widgets/CostBomDemo/strings'

const PERSONAL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'aol.com',
  'icloud.com',
  'mail.com',
  'protonmail.com',
  'yandex.com',
  'zoho.com',
  'gmx.com',
  'live.com',
  'me.com',
  'msn.com',
  'qq.com',
  '163.com',
  '126.com',
]

export type LeadSource = 'hero-cta' | 'hero-conversion' | 'cm2-cta'

export interface LeadPayload {
  email: string
  product: string
  country: string
  source: LeadSource
}

export type LeadResult = { ok: true } | { ok: false; error: string }

// Zapier catch hook (provided by marketing) that writes leads to the tracker.
const ZAPIER_LEAD_HOOK_URL =
  'https://hooks.zapier.com/hooks/catch/17821343/4uz7ozb/'

// Read any hidden inputs the GTM "UTM + Clarity enrichment" tag injects into
// the form (utm_*, gclid, Clarity replay URL) so attribution travels with the
// lead — same data the old native Webflow submit carried.
function collectFormAttribution(): Record<string, string> {
  const out: Record<string, string> = {}
  const form = document.getElementById('wf-form-Cost-Model-Form_step-2')
  if (!form) return out
  form.querySelectorAll('input[type="hidden"]').forEach((el) => {
    const input = el as HTMLInputElement
    if (input.name && input.value) out[input.name] = input.value
  })
  return out
}

// Fire-and-forget POST to Zapier. Never awaited and never throws into the UI —
// a lead form should never feel slow or error because of a network hiccup.
function postLeadToZapier(lead: LeadPayload): void {
  try {
    const payload = {
      email: lead.email,
      product: lead.product,
      country: lead.country,
      source: lead.source,
      page_url: window.location.href,
      referrer: document.referrer,
      ...collectFormAttribution(),
    }
    // text/plain keeps this a "simple" CORS request (no preflight, which the
    // Zapier hook doesn't answer); Zapier still parses the JSON body fine.
    fetch(ZAPIER_LEAD_HOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      /* swallow — capture failures must not affect the user */
    })
  } catch {
    /* defensive: never let lead capture break the form */
  }
}

/**
 * Single source of truth for the "is this a usable work email" check used by
 * the conversion form. Rejects empty/malformed addresses and known personal
 * providers.
 */
export function isWorkEmail(email: string): boolean {
  const v = email.trim().toLowerCase()
  const at = v.indexOf('@')
  if (at < 1) return false
  const domain = v.slice(at + 1)
  if (!domain || domain.indexOf('.') < 1) return false
  return !PERSONAL_DOMAINS.includes(domain)
}

export function useCostModelLead() {
  const submitLead = useCallback((lead: LeadPayload): LeadResult => {
    if (!isWorkEmail(lead.email)) {
      return {
        ok: false as const,
        error: COST_BOM_DEMO_STRINGS.businessEmailError,
      }
    }

    triggerTagManagerEvent(TagManagerEventType.costModelLeadSubmitted)
    postLeadToZapier(lead)

    return { ok: true as const }
  }, [])

  return { submitLead, isWorkEmail }
}
